import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { DEFAULT_SELECTION, type SelectionSource } from "./constants";

type SelectionContextValue = {
  getSelection: (rowKey: string) => SelectionSource;
  setSelection: (rowKey: string, source: SelectionSource) => void;
  hasNetChanges: () => boolean;
};

const SelectionContext = createContext<SelectionContextValue | null>(null);

type SelectionProviderProps = {
  children: ReactNode;
};

export const SelectionProvider = ({ children }: SelectionProviderProps) => {
  const [selections, setSelections] = useState<Map<string, SelectionSource>>(new Map());

  const getSelection = useCallback(
    (rowKey: string) => selections.get(rowKey) ?? DEFAULT_SELECTION,
    [selections],
  );

  // Only track deviations from the default so a round trip back to Proposed is a no-op.
  const setSelection = useCallback((rowKey: string, source: SelectionSource) => {
    setSelections((prev) => {
      const next = new Map(prev);
      if (source === DEFAULT_SELECTION) {
        next.delete(rowKey);
      } else {
        next.set(rowKey, source);
      }
      return next;
    });
  }, []);

  const hasNetChanges = useCallback(() => {
    for (const source of selections.values()) {
      if (source !== DEFAULT_SELECTION) return true;
    }
    return false;
  }, [selections]);

  const value = useMemo(
    () => ({ getSelection, setSelection, hasNetChanges }),
    [getSelection, setSelection, hasNetChanges],
  );

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
};

export const useSelection = (): SelectionContextValue => {
  const ctx = useContext(SelectionContext);
  if (!ctx) {
    throw new Error("useSelection must be used within a SelectionProvider");
  }
  return ctx;
};
